<template>
    <div class="relative w-full">
        <!-- Always visible filters (first N filters based on context) -->
        <div class="mb-4 flex min-w-0 gap-3">
            <template
                v-for="filter in visibleFilters"
                :key="filter.key"
            >
                <MuvalSelect
                    :model-value="props.draftFilters[filter.key]"
                    :mode="filter.singleSelect ? 'single' : 'multiple'"
                    :canSearch="filter.canSearch"
                    :rounded="filter.rounded"
                    :label="filter.label"
                    :placeholder="filter.placeholder"
                    :selectOptions="filter.selectOptions"
                    :valueProp="filter.valueProp"
                    :displayLabel="filter.displayLabel"
                    :class="[filter.class, { 'w-auto! min-w-0 flex-1': responsiveToolbar }]"
                    :highlightOnValue="filter.highlightOnValue"
                    :disabled="filter.disabled"
                    @update:model-value="(value) => updateDraftFilter(filter.key, value)"
                    @click.stop
                    @mousedown.stop
                />
            </template>

            <!-- Filter popup button (only show if there are hidden filters) -->
            <PopoverRoot
                v-if="hiddenFilters.length > 0"
                v-model:open="showModal"
            >
                <div
                    class="relative flex cursor-pointer items-center"
                    :class="{ 'shrink-0': responsiveToolbar }"
                >
                    <PopoverTrigger as-child>
                        <button
                            type="button"
                            class="h-input border-muval-gray-4 hover:border-muval-gray-3 z-10 flex items-center gap-2 rounded-full border bg-white px-2.5 transition-all"
                            :class="props.activeFilterCount > 0 ? 'w-16' : 'w-[38px]'"
                        >
                            <SvgIcon
                                name="system-filter"
                                class="h-4 w-4 shrink-0"
                            />
                            <transition
                                name="fade"
                                mode="out-in"
                            >
                                <span
                                    v-if="props.activeFilterCount > 0"
                                    class="bg-muval-brand inline-flex h-4 w-4 items-center justify-center rounded-full text-xs text-white"
                                >
                                    {{ props.activeFilterCount }}
                                </span>
                            </transition>
                        </button>
                    </PopoverTrigger>

                    <transition
                        name="slide"
                        mode="out-in"
                    >
                        <span
                            v-if="
                                activeDraftFilterCount > 0 || (shouldUseAssignedIdsMode && !props.filtersClearedByUser)
                            "
                            data-testid="clear-filter-btn"
                            class="h-input border-muval-gray-4 bg-muval-gray-5 z-1 -ml-5 inline-flex w-10 items-center justify-end rounded-full rounded-l-none border pr-0.5"
                            @click.stop="
                                shouldUseAssignedIdsMode && !props.filtersClearedByUser ?
                                    emit('clear-assigned-ids')
                                :   emit('clear-all-filters')
                            "
                        >
                            <SvgIcon
                                name="system-close"
                                class="text-muval-gray-2 h-4 w-4"
                            />
                        </span>
                    </transition>
                </div>

                <!--
                    Portalled rather than absolutely positioned inside the bar. The
                    modals this filter renders in scroll their own body, which clips
                    an absolute child and drags it out of alignment with the button
                    it belongs to - on a short viewport that put Apply out of reach.

                    `hide-when-detached` is the other half: freed of the clip, the
                    popover would otherwise keep tracking its button past the top of
                    the modal and float over the page behind it.
                -->
                <PopoverPortal>
                    <PopoverContent
                        class="border-muval-gray-4 z-100 max-h-(--reka-popper-available-height) w-80 overflow-y-auto rounded-lg border bg-white p-4 shadow-lg"
                        align="end"
                        :side-offset="8"
                        :collision-padding="16"
                        hide-when-detached
                    >
                        <div class="mb-3 flex items-center justify-between">
                            <h3 class="text-muval-gray-1 text-sm font-medium">Additional filters</h3>
                            <button
                                type="button"
                                class="text-muval-gray-3 hover:text-muval-gray-1"
                                @click.prevent="showModal = false"
                            >
                                <SvgIcon
                                    name="system-close"
                                    class="h-4 w-4"
                                />
                            </button>
                        </div>
                        <div class="space-y-3">
                            <template
                                v-for="(filter, index) in hiddenFilters"
                                :key="filter.key"
                            >
                                <!-- Add "Filter out conflicts" heading before scheduling conflicts filter -->
                                <div
                                    v-if="filter.key === 'schedulingConflicts'"
                                    class="border-muval-gray-4 border-t pt-2"
                                >
                                    <h4 class="text-muval-gray-2 mb-3 text-xs font-medium">Filter out conflicts</h4>
                                </div>

                                <!-- Add "Filter by last usage" heading before distance and action type filters -->
                                <div
                                    v-if="
                                        (filter.key === 'last_run_distance' || filter.key === 'last_run_action') &&
                                        hiddenFilters.findIndex((f) => f.key === 'last_run_distance') === index
                                    "
                                    class="border-muval-gray-4 border-t pt-2"
                                >
                                    <h4 class="text-muval-gray-2 mb-3 text-xs font-medium">Filter by last usage</h4>
                                </div>

                                <!-- Add "Filter by associated depot" heading before depot filter -->
                                <div
                                    v-if="filter.key === 'depots'"
                                    class="border-muval-gray-4 border-t pt-2"
                                >
                                    <h4 class="text-muval-gray-2 mb-3 text-xs font-medium">
                                        Filter by associated depot
                                    </h4>
                                </div>

                                <!-- Add "Filter by borrowed/lease provider" heading before affiliate filter -->
                                <div
                                    v-if="filter.key === 'affiliates'"
                                    class="border-muval-gray-4 border-t pt-2"
                                >
                                    <h4 class="text-muval-gray-2 mb-3 text-xs font-medium">
                                        Filter by borrowed/lease provider
                                    </h4>
                                </div>

                                <!-- Add "Show assigned resources" heading before assigned ids checkbox -->
                                <div
                                    v-if="filter.key === 'showAssignedIds'"
                                    class="border-muval-gray-4 border-t pt-2"
                                >
                                    <h4 class="text-muval-gray-2 mb-3 text-xs font-medium">Show assigned resources</h4>
                                </div>

                                <!-- Checkbox filter for Assigned Ids -->
                                <div
                                    v-if="filter.type === 'checkbox'"
                                    class="flex items-center gap-2"
                                >
                                    <MuvalCheckbox
                                        :model-value="props.draftFilters[filter.key] || false"
                                        :label="filter.label"
                                        @update:model-value="(value) => updateDraftFilter(filter.key, value)"
                                    />
                                </div>
                                <!--
                                    Regular select filters. `append-to-body` is off
                                    in here: the popover dismisses on a pointer-down
                                    outside its own DOM, and a dropdown appended to
                                    the body counts as outside - picking an option
                                    would shut the popover instead of filtering.
                                -->
                                <MuvalSelect
                                    v-else
                                    :append-to-body="false"
                                    :model-value="props.draftFilters[filter.key]"
                                    :mode="filter.singleSelect ? 'single' : 'multiple'"
                                    :canSearch="filter.canSearch"
                                    :rounded="filter.rounded"
                                    :label="filter.label"
                                    :placeholder="filter.placeholder"
                                    :selectOptions="filter.selectOptions"
                                    :valueProp="filter.valueProp"
                                    :displayLabel="filter.displayLabel"
                                    :class="filter.class"
                                    :highlightOnValue="filter.highlightOnValue"
                                    :disabled="filter.disabled"
                                    @update:model-value="(value) => updateDraftFilter(filter.key, value)"
                                    @click.stop
                                    @mousedown.stop
                                />
                            </template>
                        </div>
                        <div class="mt-4 flex items-center justify-between">
                            <span class="text-muval-gray-2 text-xs">
                                {{ activeDraftFilterCount }} filter{{ activeDraftFilterCount > 1 ? 's' : '' }} selected
                            </span>
                            <div class="flex gap-2">
                                <button
                                    type="button"
                                    class="text-muval-gray-2 hover:text-muval-gray-1 px-3 py-1 text-xs"
                                    @click="clearHiddenFilters"
                                >
                                    Clear
                                </button>
                                <button
                                    type="button"
                                    class="hover:bg-muval-brand-dark bg-muval-brand rounded-full px-3 py-1 text-xs text-white"
                                    @click="applyHiddenFilters"
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </PopoverContent>
                </PopoverPortal>
            </PopoverRoot>

            <!-- Refresh Button -->
            <MuvalButton
                type="button"
                tertiary
                :circle="responsiveToolbar"
                :class="{ 'aspect-square w-fit! shrink-0': responsiveToolbar }"
                :loading="isLoading || isFetchingNextPage"
                data-resource-refresh
                @click="refetch"
            >
                <SvgIcon
                    name="system-refresh"
                    class="h-4 w-4"
                />
                <span class="sr-only">Refresh resources</span>
            </MuvalButton>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from 'reka-ui';
import MuvalSelect from '@components/inputs/MuvalSelect.vue';
import MuvalCheckbox from '@components/inputs/MuvalCheckbox.vue';
import MuvalButton from '@components/button/MuvalButton.vue';
import SvgIcon from '@components/utilities/SvgIcon.vue';

// Track if showAssignedIds was explicitly cleared by user
const showAssignedIdsExplicitlyCleared = ref(false);

const props = defineProps({
    // Modal context determines which filters are available and their order
    context: {
        type: String,
        required: true,
        validator: (value) => ['container', 'trailer', 'transport'].includes(value),
    },
    // Default filters for this context
    defaultFilters: {
        type: Object,
        required: true,
    },
    // Auto-sync filters (always visible)
    autoSyncFilters: {
        type: Array,
        default: () => [],
    },
    // Loading states
    isLoading: {
        type: Boolean,
        default: false,
    },
    isFetchingNextPage: {
        type: Boolean,
        default: false,
    },
    // Refetch function
    refetch: {
        type: Function,
        required: true,
    },
    // Event data for context-specific options
    eventData: {
        type: Object,
        default: () => ({}),
    },
    // Filter options data
    filterOptions: {
        type: Object,
        required: true,
    },
    // Filter state from parent
    activeFilters: {
        type: Object,
        required: true,
    },
    draftFilters: {
        type: Object,
        required: true,
    },
    activeFilterCount: {
        type: Number,
        required: true,
    },
    // Pre-selected item IDs (determines if default filters should be applied)
    assignedIds: {
        type: Array,
        default: () => [],
    },
    // Hide capacity-related filters for unsupported event types
    hideCapacity: {
        type: Boolean,
        default: false,
    },
    // Whether the user has explicitly cleared filters (hides the assigned-ids X button)
    filtersClearedByUser: {
        type: Boolean,
        default: false,
    },
    /**
     * Restrict the filter set to these keys, in the component's own order.
     *
     * Null - the operations assign modals - renders every filter. The company
     * diary's vehicle picker passes a subset, because its availability endpoint
     * answers none of the run-relative questions (scheduling conflicts, last usage)
     * and a control that silently does nothing is worse than an absent one.
     */
    availableFilters: {
        type: Array,
        default: null,
    },
    // Let compact callers give the visible select the remaining row width while
    // keeping the filter and refresh actions at their intrinsic size.
    responsiveToolbar: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:active-filters', 'update:draft-filters', 'clear-all-filters', 'clear-assigned-ids']);

// Modal state
const showModal = ref(false);

// Context-specific options
const schedulingConflictOptions = [
    { value: 'all', label: 'All conflicts' },
    { value: 'outside_run', label: 'Only conflicts outside this run' },
    { value: 'inside_run', label: 'Only conflicts inside this run' },
];

const trailerReadinessDistanceOptions = [
    { value: '10', label: '≤ 10km from this event' },
    { value: '50', label: '≤ 50km from this event' },
    { value: '100', label: '≤ 100km from this event' },
];

// Dynamic capacity options based on metadata.space
const capacityOptions = computed(() => {
    const space = props.eventData?.metadata?.space || 0;
    return [
        { value: 'gte', label: `≥ ${space}m³` },
        { value: 'lte', label: `≤ ${space}m³` },
    ];
});

// Define unified filter configuration for all contexts
const filterConfigs = computed(() => {
    // Check if current body type selection enables capacity filter
    const isCapacityEnabled = computed(() => {
        const selectedTypes = Array.isArray(props.draftFilters.types) ? props.draftFilters.types : [];

        if (props.context === 'transport') {
            // For transport: only enabled for specific types (PANTECH, HUNGRY_BOX, VAN, UTE)
            const capacityEnabledTypes = ['PANTECH', 'HUNGRY_BOX', 'VAN', 'UTE'];
            return selectedTypes.some((type) => capacityEnabledTypes.includes(type));
        } else if (props.context === 'container') {
            // For containers: always enabled
            return true;
        } else if (props.context === 'trailer') {
            // For trailers: only enabled if ENCLOSED_TRAILER subtype is selected
            const selectedSubtypes = props.draftFilters.subtypes || [];
            if (Array.isArray(selectedSubtypes)) {
                return selectedSubtypes.includes('ENCLOSED_TRAILER');
            }
            return selectedSubtypes === 'ENCLOSED_TRAILER';
        }

        return false;
    });

    const filters = [
        // 1. Body type (visible)
        {
            key:
                props.context === 'container' ? 'subtypes'
                : props.context === 'trailer' ? 'subtypes'
                : 'types',
            singleSelect: false,
            canSearch: false,
            rounded: true,
            label:
                props.context === 'container' ? 'Subtypes'
                : props.context === 'trailer' ? 'Subtypes'
                : 'Transport type',
            placeholder:
                props.context === 'container' ? 'All subtypes'
                : props.context === 'trailer' ? 'All subtypes'
                : 'All transport types',
            selectOptions:
                props.context === 'container' ? props.filterOptions.subtypeOptions
                : props.context === 'trailer' ? props.filterOptions.subtypeOptions
                : props.filterOptions.typeOptions,
            valueProp: 'id',
            displayLabel: 'name',
            /* Wide enough for "All transport types", the longest placeholder it carries. */
            class: 'w-48',
            highlightOnValue: true,
        },
    ];

    // 2. Available capacity (visible, conditionally enabled) - only for capacity-supported event types
    if (!props.hideCapacity) {
        filters.push({
            key: 'availableCapacity',
            singleSelect: true,
            canSearch: false,
            rounded: true,
            label: 'Available Capacity',
            placeholder: 'No capacity filter',
            selectOptions: capacityOptions,
            valueProp: 'value',
            displayLabel: 'label',
            class: 'w-48',
            highlightOnValue: true,
            disabled: !isCapacityEnabled.value,
        });
    }

    const visibleCount = props.hideCapacity ? 1 : 2;

    filters.push(
        // 3. Scheduling conflicts (hidden)
        {
            key: 'schedulingConflicts',
            singleSelect: true,
            canSearch: false,
            rounded: true,
            label: 'Scheduling',
            placeholder: 'Select scheduling filter',
            selectOptions: schedulingConflictOptions,
            valueProp: 'value',
            displayLabel: 'label',
            class: 'w-48',
            highlightOnValue: true,
        },
        // 4. Last use - Distance (hidden)
        {
            key: 'last_run_distance',
            singleSelect: true,
            canSearch: false,
            rounded: true,
            label: 'Distance',
            placeholder: 'Select distance',
            selectOptions: trailerReadinessDistanceOptions,
            valueProp: 'value',
            displayLabel: 'label',
            class: 'w-48',
            highlightOnValue: true,
        },
        // 5. Last use - Action type (hidden)
        {
            key: 'last_run_action',
            singleSelect: true,
            canSearch: false,
            rounded: true,
            label: 'Task type',
            placeholder: 'Select task type',
            selectOptions: props.filterOptions.trailerReadinessActionTypeOptions,
            valueProp: 'value',
            displayLabel: 'label',
            class: 'w-48',
            highlightOnValue: true,
        },
        // 6. Depots (hidden)
        {
            key: 'depots',
            singleSelect: false,
            canSearch: false,
            rounded: true,
            label: 'Depots',
            placeholder: 'All depots',
            selectOptions: props.filterOptions.depotOptions,
            valueProp: 'id',
            displayLabel: 'name',
            class: 'w-40',
            highlightOnValue: true,
        },
        // 7. Affiliates (hidden)
        {
            key: 'affiliates',
            singleSelect: false,
            canSearch: false,
            rounded: true,
            label: 'Affiliates',
            placeholder: 'All affiliates',
            selectOptions: props.filterOptions.affiliateOptions,
            valueProp: 'id',
            displayLabel: 'name',
            class: 'w-40',
            highlightOnValue: true,
        },
        // 8. Assigned Ids (hidden, checkbox)
        {
            key: 'showAssignedIds',
            type: 'checkbox',
            label: 'Assigned Ids',
            class: 'w-full',
        },
    );

    if (props.availableFilters === null) {
        return { visibleCount, filters };
    }

    const allowed = filters.filter((filter) => props.availableFilters.includes(filter.key));

    return {
        // The always-visible slots are the retained filters among the original
        // leading ones, so dropping a hidden filter cannot promote another into
        // the bar and dropping a visible one cannot leave an empty slot.
        visibleCount: filters.slice(0, visibleCount).filter((filter) => props.availableFilters.includes(filter.key))
            .length,
        filters: allowed,
    };
});

// Get current filter configuration
const currentConfig = computed(() => {
    return filterConfigs.value;
});

// Split filters into visible and hidden
const visibleFilters = computed(() => {
    return currentConfig.value.filters.slice(0, currentConfig.value.visibleCount);
});

const hiddenFilters = computed(() => {
    const allHiddenFilters = currentConfig.value.filters.slice(currentConfig.value.visibleCount);
    // Only show showAssignedIds filter when assignedIds exist
    return allHiddenFilters.filter((filter) => {
        if (filter.key === 'showAssignedIds') {
            return props.assignedIds && props.assignedIds.length > 0;
        }
        return true;
    });
});

// Determine if we should use assignedIds mode (no default filters, send assigned_ids[] to endpoint)
const shouldUseAssignedIdsMode = computed(() => {
    return props.assignedIds && props.assignedIds.length > 0;
});

// Check if there are any active filters
const hasActiveFilters = computed(() => {
    return Object.values(props.activeFilters).some((value) => {
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return value !== null && value !== undefined && value !== '';
    });
});

// Count active draft filters (for modal display)
const activeDraftFilterCount = computed(() => {
    let count = 0;
    Object.entries(props.draftFilters).forEach(([key, value]) => {
        // Skip showAssignedIds from draft count (it's handled separately in activeFilterCount)
        if (key === 'showAssignedIds') {
            return;
        }
        if (Array.isArray(value)) {
            if (value.length > 0) {
                count++;
            }
        } else if (value !== null && value !== undefined && value !== '') {
            count++;
        }
    });
    return count;
});

// Update draft filter
const updateDraftFilter = (key, value) => {
    // If user manually enables showAssignedIds, reset the cleared flag
    if (key === 'showAssignedIds' && value === true) {
        showAssignedIdsExplicitlyCleared.value = false;
    }

    const newDraftFilters = { ...props.draftFilters };

    // Ensure multi-select filters are always arrays
    if ((key === 'types' || key === 'affiliates' || key === 'depots') && !Array.isArray(value)) {
        newDraftFilters[key] = value ? [value] : [];
    } else {
        newDraftFilters[key] = value;
    }

    // If types filter is being updated and capacity filter becomes disabled, clear capacity selection
    if (key === 'types' && props.context === 'transport') {
        const selectedTypes = newDraftFilters[key]; // Use the processed value (now guaranteed to be array)
        const capacityEnabledTypes = ['PANTECH', 'HUNGRY_BOX', 'VAN', 'UTE'];
        const hasCapacityEnabledType = selectedTypes.some((type) => capacityEnabledTypes.includes(type));

        if (!hasCapacityEnabledType) {
            // Clear available capacity selection when no capacity-enabled types are selected
            newDraftFilters.availableCapacity = null;
        }
    }

    emit('update:draft-filters', newDraftFilters);
};

// Clear only hidden filters
const clearHiddenFilters = () => {
    const newActiveFilters = { ...props.activeFilters };
    const newDraftFilters = { ...props.draftFilters };

    hiddenFilters.value.forEach((filter) => {
        if (Array.isArray(newActiveFilters[filter.key])) {
            newActiveFilters[filter.key] = [];
            newDraftFilters[filter.key] = [];
        } else {
            newActiveFilters[filter.key] = null;
            newDraftFilters[filter.key] = null;
        }
    });

    // If we have assignedIds and no active filters after clearing,
    // we should go back to assignedIds mode (no filters, use assigned_ids[] in endpoint)

    emit('update:active-filters', newActiveFilters);
    emit('update:draft-filters', newDraftFilters);
};

// Apply hidden filters
const applyHiddenFilters = () => {
    showModal.value = false;
    emit('update:active-filters', props.draftFilters);
};

// Clear all filters (both visible and hidden)
const clearAllFilters = () => {
    const newActiveFilters = { ...props.activeFilters };
    const newDraftFilters = { ...props.draftFilters };

    // Mark that showAssignedIds was explicitly cleared
    showAssignedIdsExplicitlyCleared.value = true;

    // Clear all filters
    Object.keys(newActiveFilters).forEach((key) => {
        if (key === 'showAssignedIds') {
            // For showAssignedIds, set to false (not null) to properly clear it
            newActiveFilters[key] = false;
            newDraftFilters[key] = false;
        } else if (Array.isArray(newActiveFilters[key])) {
            newActiveFilters[key] = [];
            newDraftFilters[key] = [];
        } else {
            newActiveFilters[key] = null;
            newDraftFilters[key] = null;
        }
    });

    // If we have assignedIds, we should go back to assignedIds mode

    emit('update:active-filters', newActiveFilters);
    emit('update:draft-filters', newDraftFilters);
    emit('clear-all-filters');
};

// Watch assignedIds and update showAssignedIds filter accordingly
// But respect when user has explicitly cleared it
// Use a flag to prevent recursive updates during filter changes
let isUpdatingShowAssignedIds = false;

watch(
    () => props.assignedIds,
    (newAssignedIds, oldAssignedIds) => {
        // Prevent updates if we're already updating or if the array reference hasn't actually changed
        if (isUpdatingShowAssignedIds) {
            return;
        }

        // Check if the arrays are actually different (not just reference)
        const newIds = newAssignedIds || [];
        const oldIds = oldAssignedIds || [];
        const arraysEqual = newIds.length === oldIds.length && newIds.every((id, index) => id === oldIds[index]);

        if (arraysEqual && oldIds.length > 0) {
            // Arrays are the same, skip update
            return;
        }

        const hasAssignedIds = newIds.length > 0;
        const currentDraftValue = props.draftFilters.showAssignedIds;
        const currentActiveValue = props.activeFilters.showAssignedIds;

        isUpdatingShowAssignedIds = true;

        try {
            // If explicitly cleared, ensure it stays false
            if (showAssignedIdsExplicitlyCleared.value) {
                if (currentDraftValue !== false || currentActiveValue !== false) {
                    updateDraftFilter('showAssignedIds', false);
                    emit('update:active-filters', {
                        ...props.activeFilters,
                        showAssignedIds: false,
                    });
                }
                return; // Don't auto-set it back to true
            }

            if (!hasAssignedIds) {
                // No assignedIds - always set to false and reset the cleared flag
                if (currentDraftValue !== false || currentActiveValue !== false) {
                    updateDraftFilter('showAssignedIds', false);
                    // Only emit if the value actually needs to change
                    if (currentActiveValue !== false) {
                        emit('update:active-filters', {
                            ...props.activeFilters,
                            showAssignedIds: false,
                        });
                    }
                }
                showAssignedIdsExplicitlyCleared.value = false; // Reset flag when no assignedIds
            } else if (hasAssignedIds) {
                // assignedIds exist and not explicitly cleared - set to true if it's not already true
                if (currentDraftValue !== true || currentActiveValue !== true) {
                    updateDraftFilter('showAssignedIds', true);
                    // Only emit if the value actually needs to change
                    if (currentActiveValue !== true) {
                        emit('update:active-filters', {
                            ...props.activeFilters,
                            showAssignedIds: true,
                        });
                    }
                }
            }
        } finally {
            // Use nextTick to ensure the flag is reset after all updates complete
            setTimeout(() => {
                isUpdatingShowAssignedIds = false;
            }, 0);
        }
    },
    { immediate: true },
);

// Also watch activeFilters.showAssignedIds to ensure it stays false when explicitly cleared
watch(
    () => props.activeFilters.showAssignedIds,
    (newValue) => {
        if (showAssignedIdsExplicitlyCleared.value && newValue === true) {
            // User explicitly cleared it, but something set it back to true - force it back to false
            emit('update:active-filters', {
                ...props.activeFilters,
                showAssignedIds: false,
            });
        }
    },
);

// Expose functions for parent components
defineExpose({
    updateDraftFilter,
    clearHiddenFilters,
    applyHiddenFilters,
    clearAllFilters,
    shouldUseAssignedIdsMode,
    hasActiveFilters,
});
</script>
